import { useState, useEffect, useMemo } from "react";
import { Input, Autocomplete, AutocompleteItem } from "@heroui/react";
import { useFetchData } from "@/hooks";
import { ThemedText } from "@/components/ui";

// Fallback country codes if API fails
const FALLBACK_COUNTRY_CODES = [
    { value: "62", title: "Indonesia", iso: "ID" },
    { value: "60", title: "Malaysia", iso: "MY" },
    { value: "65", title: "Singapore", iso: "SG" },
    { value: "1", title: "United States", iso: "US" },
    { value: "44", title: "United Kingdom", iso: "GB" },
];

/**
 * PhoneInput Component
 * Combines country code autocomplete with phone number input
 * Automatically strips leading 0 from phone number
 *
 * @param {Object} props
 * @param {string} props.value - Full phone number with country code (e.g., "+6281234567890")
 * @param {Function} props.onChange - Callback with full phone number
 * @param {string} props.label - Input label
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.size - Input size (sm, md, lg)
 * @param {boolean} props.isInvalid - Show error state
 * @param {string} props.errorMessage - Error message to display
 */
const PhoneInput = ({
    value = "",
    onChange,
    placeholder = "8123456789",
    size = "md",
    isInvalid = false,
    errorMessage = "",
    autocompleteProps = {},
    autocompleteItemProps = {},
    inputProps = {},
}) => {
    // Fetch country codes from API
    const countryCodesData = useFetchData("select/country-code");
    const countryCodes = countryCodesData.data || FALLBACK_COUNTRY_CODES;

    // Convert API format to internal format with + prefix
    const formattedCodes = useMemo(() => {
        return countryCodes.map((cc) => ({
            code: `+${cc.value}`,
            country: cc.iso,
            label: cc.title,
            // Search string includes all variants but won't be displayed
            searchValue: `+${cc.value} ${cc.iso} ${cc.title}`.toLowerCase(),
        }));
    }, [countryCodes]);

    // Parse initial value to separate country code and number
    // Stored values don't have + prefix (e.g., "6281234567890")
    const parseValue = (fullNumber) => {
        if (!fullNumber) return { countryCode: "+62", number: "" };

        const numStr = String(fullNumber);

        // Find matching country code (try longest match first)
        const sortedCodes = [...formattedCodes].sort(
            (a, b) => b.code.length - a.code.length,
        );
        for (const cc of sortedCodes) {
            // Check both with and without + prefix
            const codeWithoutPlus = cc.code.replace("+", "");
            if (numStr.startsWith(codeWithoutPlus)) {
                return {
                    countryCode: cc.code, // Keep + for display
                    number: numStr.slice(codeWithoutPlus.length),
                };
            }
            if (numStr.startsWith(cc.code)) {
                return {
                    countryCode: cc.code,
                    number: numStr.slice(cc.code.length),
                };
            }
        }
        // Default to Indonesia if no match
        return { countryCode: "+62", number: numStr };
    };

    const [countryCode, setCountryCode] = useState("+62");
    const [phoneNumber, setPhoneNumber] = useState("");

    // Update internal state when value prop changes
    useEffect(() => {
        const parsed = parseValue(value);
        setCountryCode(parsed.countryCode);
        setPhoneNumber(parsed.number);
    }, [value, formattedCodes]);

    // Handle country code change
    const handleCountryCodeChange = (newCode) => {
        if (newCode) {
            setCountryCode(newCode);
            // Trigger onChange without + prefix for storage
            if (onChange && phoneNumber) {
                onChange(`${newCode.replace("+", "")}${phoneNumber}`);
            }
        }
    };

    // Handle phone number change
    const handleNumberChange = (newNumber) => {
        // Remove any non-digit characters except for initial input
        let cleaned = newNumber.replace(/[^\d]/g, "");

        // Strip leading 0 (since country code already has the prefix)
        if (cleaned.startsWith("0")) {
            cleaned = cleaned.slice(1);
        }

        setPhoneNumber(cleaned);

        // Trigger onChange without + prefix for storage
        if (onChange) {
            onChange(
                cleaned ? `${countryCode.replace("+", "")}${cleaned}` : "",
            );
        }
    };

    return (
        <div className="flex flex-col gap-1">
            <div className="flex gap-2">
                {/* Country Code Autocomplete */}
                <Autocomplete
                    aria-label="Kode Negara"
                    selectedKey={countryCode}
                    onSelectionChange={handleCountryCodeChange}
                    className="w-36 shrink-0"
                    size={size}
                    label="Kode"
                    variant="bordered"
                    isLoading={countryCodesData.isLoading}
                    defaultItems={formattedCodes}
                    isClearable={false}
                    defaultFilter={(textValue, inputValue) => {
                        // Find the item and search in searchValue
                        const item = formattedCodes.find(
                            (cc) => `${cc.code} ${cc.country}` === textValue,
                        );
                        if (!item) return true;
                        return item.searchValue.includes(
                            inputValue.toLowerCase(),
                        );
                    }}
                    {...autocompleteProps}
                >
                    {(cc) => (
                        <AutocompleteItem
                            key={cc.code}
                            textValue={`${cc.code} ${cc.country}`}
                            {...autocompleteItemProps}
                        >
                            <div className="flex items-end gap-1">
                                <ThemedText size="sm" className="font-medium">
                                    {cc.code}
                                </ThemedText>
                                <ThemedText size="xxs" variant="secondary">
                                    {cc.country}
                                </ThemedText>
                            </div>
                        </AutocompleteItem>
                    )}
                </Autocomplete>

                {/* Phone Number Input */}
                <Input
                    type="tel"
                    value={phoneNumber}
                    onValueChange={handleNumberChange}
                    placeholder={placeholder}
                    variant="bordered"
                    label="Nomor"
                    size={size}
                    isInvalid={isInvalid}
                    className="flex-1"
                    {...inputProps}
                />
            </div>
            {isInvalid && errorMessage && (
                <ThemedText size="xs" variant="danger">
                    {errorMessage}
                </ThemedText>
            )}
        </div>
    );
};

export default PhoneInput;
